import { supabase } from '../supabaseClient';

// Scalar fields the admin can resolve field-by-field. For each, the default is
// to keep the primary's value (and only fill it when the primary is blank);
// passing resolved[field] === 'duplicate' forces the duplicate's value.
export const MERGE_SCALAR_FIELDS = [
  'patient_name', 'patient_email', 'patient_dob', 'patient_phone', 'patient_address',
  'patient_street', 'patient_city', 'patient_state', 'patient_zip',
  'doctor_name', 'doctor_email', 'doctor_phone', 'doctor_address',
  'lab_id', 'blood_collection_time', 'insurance_company', 'insurance_policy_number',
  'lab_results_url', 'fedex_label_url', 'fedex_tracking_number',
  'script_url', 'insurance_card_url', 'patient_id_url',
  'outcome_code', 'outcome_notes', 'outcome_logged_at', 'status',
];

// Array/image fields are always unioned (deduped), never a choice.
const MERGE_ARRAY_FIELDS = ['script_image', 'insurance_card_image', 'patient_id_image', 'tags'];

const isBlank = (v) => v === null || v === undefined || v === '';

// Combine the "Order ID" (lab_order_number) values from any number of
// submissions into one deduped, comma-joined string — e.g. "12345, 67890".
// Splits existing values on , ; / so merging an already-merged record doesn't
// pile up duplicates. Order is preserved (first-seen wins).
export function combineOrderIds(...values) {
  const seen = new Set();
  const out = [];
  for (const v of values) {
    for (const part of String(v ?? '').split(/[,;/]+/)) {
      const t = part.trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out.join(', ');
}

// Tests for a submission, with a safety net: use the structured
// tests_on_the_script when present, otherwise parse the "Tests: A, B, C" lines
// out of special_instructions (how LDX imports record them) so a merge never
// loses tests that only exist as text. Returns [{ name, code? }].
export function testsFromSubmission(sub) {
  const arr = Array.isArray(sub?.tests_on_the_script) ? sub.tests_on_the_script.filter(Boolean) : [];
  if (arr.length) return arr;
  const si = sub?.special_instructions || '';
  const names = [];
  const re = /tests?:\s*(.+)/gi;
  let m;
  while ((m = re.exec(si)) !== null) {
    m[1].split(/[,;]/).forEach((n) => {
      const t = n.trim();
      if (t) names.push({ name: t });
    });
  }
  return names;
}

// Compute which scalar fields actually conflict (both non-blank and different),
// so the UI only asks about real conflicts.
export function mergeConflicts(primary, duplicate) {
  return MERGE_SCALAR_FIELDS.filter(
    (f) => !isBlank(primary?.[f]) && !isBlank(duplicate?.[f]) && String(primary[f]) !== String(duplicate[f])
  );
}

/**
 * Merge `duplicate` into `primary`.
 * - Status notes (submission_status_events) move from duplicate → primary.
 * - Blank primary fields are filled from the duplicate; conflicting fields use
 *   `resolved[field]` ('primary' | 'duplicate'), defaulting to primary.
 * - Image/tag arrays are unioned; special_instructions are concatenated.
 * - Lab links (inbound orders + outbound queue) are re-pointed to the primary.
 * - The duplicate is set status='cancelled', merged_into=primary.id, and an
 *   audit status event is written on the primary.
 */
export async function mergeSubmissions({ primary, duplicate, resolved = {}, mergeTests = false }) {
  if (!primary?.id || !duplicate?.id) throw new Error('Both submissions are required.');
  if (primary.id === duplicate.id) throw new Error('Pick two different submissions to merge.');

  const updates = {};

  // Tests on the script — union by test id, but only when the admin opts in
  // (mergeTests). The kept submission keeps its own tests (its version wins for
  // a shared test) and gains any of the duplicate's it doesn't already have.
  // Fully deduped by id, so no test can appear twice — including any internal
  // duplicates already present on either side.
  if (mergeTests) {
    const primaryTests = Array.isArray(primary.tests_on_the_script) ? primary.tests_on_the_script : [];
    const dupTests = Array.isArray(duplicate.tests_on_the_script) ? duplicate.tests_on_the_script : [];
    // Dedupe key: prefer id, but LDX-imported tests have no id — fall back to
    // name/code so they aren't dropped. Unkeyable tests are always kept.
    const seen = new Set();
    const merged = [];
    for (const t of [...primaryTests, ...dupTests]) {
      if (!t) continue;
      const key = t.id != null ? `id:${t.id}`
        : t.name ? `nm:${String(t.name).trim().toLowerCase()}`
        : t.code ? `cd:${String(t.code).trim().toLowerCase()}`
        : null;
      if (key && seen.has(key)) continue; // first occurrence wins (primary first)
      if (key) seen.add(key);
      merged.push(t);
    }
    // Only write when it actually changed (gained a test or dropped a dupe).
    if (merged.length !== primaryTests.length) updates.tests_on_the_script = merged;
  }

  for (const f of MERGE_SCALAR_FIELDS) {
    if (resolved[f] === 'duplicate') {
      updates[f] = duplicate[f];
    } else if (isBlank(primary[f]) && !isBlank(duplicate[f])) {
      updates[f] = duplicate[f]; // fill blanks even without an explicit choice
    }
  }

  for (const f of MERGE_ARRAY_FIELDS) {
    const a = Array.isArray(primary[f]) ? primary[f] : [];
    const b = Array.isArray(duplicate[f]) ? duplicate[f] : [];
    const union = [...new Set([...a, ...b].filter(Boolean))];
    if (union.length) updates[f] = union;
  }

  // Order IDs (lab_order_number) — union both sides so the phleb + admin see
  // EVERY order number on the surviving submission (labels, search, detail).
  // This is a display/reference field only: outbound status pushes route by
  // ldx_message_id (the survivor keeps its single one), and lab_order_number is
  // not a lifecycle trigger field — so combining these never fans status updates
  // out to more than one order. Split on , ; / so repeat merges stay deduped.
  {
    const orderIds = combineOrderIds(primary.lab_order_number, duplicate.lab_order_number);
    if (orderIds && orderIds !== (primary.lab_order_number || '')) {
      updates.lab_order_number = orderIds;
    }
  }

  if (duplicate.special_instructions && duplicate.special_instructions !== primary.special_instructions) {
    updates.special_instructions = [primary.special_instructions, `[merged from #${duplicate.id}]`, duplicate.special_instructions]
      .filter(Boolean)
      .join('\n');
  }

  // 1) Apply field updates to the primary.
  if (Object.keys(updates).length) {
    const { error } = await supabase.from('submissions').update(updates).eq('id', primary.id);
    if (error) throw error;
  }

  // 2) Move all status notes from the duplicate onto the primary.
  {
    const { error } = await supabase.from('submission_status_events').update({ submission_id: primary.id }).eq('submission_id', duplicate.id);
    if (error) throw error;
  }

  // 3) Audit trail on the primary.
  await supabase.from('submission_status_events').insert({
    submission_id: primary.id,
    outcome_notes: `Merged submission #${duplicate.id} into #${primary.id}.`,
    status: updates.status || primary.status || null,
    logged_by_kind: 'admin',
  });

  // 4) Retire the duplicate. Flipping its status to 'cancelled' fires the
  //    ldx_enqueue_* AFTER UPDATE trigger, which inserts a fresh
  //    ldx_outbound_queue row tied to the DUPLICATE. This must run BEFORE the
  //    lab-link re-point below so that trigger-created row gets swept onto the
  //    primary too — otherwise it strands a queue row on the cancelled record.
  const { error: dErr } = await supabase
    .from('submissions')
    .update({ status: 'cancelled', merged_into: primary.id })
    .eq('id', duplicate.id);
  if (dErr) throw dErr;

  // 5) Re-point every lab link from the duplicate to the survivor — done LAST,
  //    after the cancel, so it also catches the outbound row the cancel just
  //    enqueued. After this the duplicate owns no inbound orders or queued
  //    pushes; everything is on the primary.
  {
    const { error: e1 } = await supabase.from('ldx_inbound_orders').update({ submission_id: primary.id }).eq('submission_id', duplicate.id);
    if (e1) throw e1;
    const { error: e2 } = await supabase.from('ldx_outbound_queue').update({ submission_id: primary.id }).eq('submission_id', duplicate.id);
    if (e2) throw e2;
  }

  return { ok: true, fieldsUpdated: Object.keys(updates) };
}
